
import { useCallback } from 'react';
import { fetchEmailCounts, invalidateEmailFolderRequests, syncEmailsFromServer } from '@/services/api/email';
import { useNotifications } from '@/context/NotificationContext';
import { EmailStatistics } from '../types';
import { emailCacheManager } from './utils/emailCacheManager';

interface UseEmailSyncOperationsProps {
  fetchEmails: (folder?: string, limit?: number, page?: number, append?: boolean, force?: boolean, search?: string) => Promise<any[]>;
  loadStatistics: (force?: boolean) => Promise<EmailStatistics>;
  currentFolder: string; // Add currentFolder to get the active folder
}

// POST /emails/sync returns 202 Accepted — the IMAP sync runs in the
// background. We wait for it by polling /emails/counts (cache-busted) and
// by listening for the backend's "Email Sync Completed" notification on the
// notifications WebSocket, whichever comes first.
const SYNC_POLL_INTERVAL_MS = 2000;
const SYNC_TIMEOUT_MS = 15000;

export const useEmailSyncOperations = ({
  fetchEmails,
  loadStatistics,
  currentFolder, // Accept currentFolder from context
}: UseEmailSyncOperationsProps) => {
  const { addRawMessageListener, removeRawMessageListener } = useNotifications();

  const syncWithServer = useCallback(async (accountId?: number): Promise<number> => {
    const folder = currentFolder;

    // One-shot listener for the backend's sync-completed WS notification
    let wsCompleted = false;
    const onWsMessage = (data: any) => {
      const type = String(data?.type || '').toLowerCase();
      const message = String(data?.message || data?.title || '').toLowerCase();
      if (
        type.includes('email_sync') ||
        (message.includes('email') && message.includes('sync') && (message.includes('complet') || message.includes('finish')))
      ) {
        wsCompleted = true;
      }
    };

    try {
      // Baseline counts for change detection (bypass the dedup cache)
      invalidateEmailFolderRequests(folder);
      const baseline = await fetchEmailCounts(undefined, accountId);
      const beforeFolder = Array.isArray(baseline) ? baseline.find((f) => f.name === folder) : undefined;
      const beforeTotal = beforeFolder?.total_count ?? 0;
      const beforeUnread = beforeFolder?.unread_count ?? 0;

      addRawMessageListener(onWsMessage);
      try {
        // 202 Accepted — actual sync happens in the background
        await syncEmailsFromServer({
          folder,
          ...(accountId && { accountId })
        });

        // Poll counts until they change, the WS completion event arrives, or timeout
        const deadline = Date.now() + SYNC_TIMEOUT_MS;
        let latestTotal = beforeTotal;
        let latestUnread = beforeUnread;

        while (Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, SYNC_POLL_INTERVAL_MS));

          invalidateEmailFolderRequests(folder); // bust dedup cache before each poll
          try {
            const counts = await fetchEmailCounts(undefined, accountId);
            const current = Array.isArray(counts) ? counts.find((f) => f.name === folder) : undefined;
            latestTotal = current?.total_count ?? latestTotal;
            latestUnread = current?.unread_count ?? latestUnread;

            if (latestTotal !== beforeTotal || latestUnread !== beforeUnread) {
              break;
            }
          } catch (pollError) {
            console.warn('Counts poll failed while waiting for sync:', pollError);
          }

          if (wsCompleted) {
            break;
          }
        }

        emailCacheManager.clearAllCache();
        invalidateEmailFolderRequests(folder);
        return Math.max(0, latestTotal - beforeTotal);
      } finally {
        removeRawMessageListener(onWsMessage);
      }
    } catch (error: any) {
      if (error.message === 'NO_EMAIL_ACCOUNTS') {
        throw new Error('No email accounts configured. Please set up your email account in settings.');
      }
      console.error('Failed to sync with server:', error);
      throw error;
    }
  }, [currentFolder, addRawMessageListener, removeRawMessageListener]);

  const refreshEmails = useCallback(async () => {
    try {
      // Clear cache for the current folder to force fresh fetch
      emailCacheManager.clearFolderCache(currentFolder);
      invalidateEmailFolderRequests(currentFolder);

      // Preserve the active search query (it lives in the URL) so a refresh
      // doesn't silently drop the user's filter.
      const activeSearch = new URLSearchParams(window.location.search).get('q') || undefined;

      // force=true bypasses the folder cache AND the in-flight request guard,
      // so the refresh never reuses a response that started before a mutation.
      await Promise.all([
        fetchEmails(currentFolder, 25, 1, false, true, activeSearch),
        loadStatistics(true),
      ]);
    } catch (error) {
      console.error('Failed to refresh emails:', error);
      throw error;
    }
  }, [fetchEmails, loadStatistics, currentFolder]);

  return {
    syncWithServer,
    refreshEmails,
  };
};
